// frontend/contexts/ProfileContext.tsx

import React, { createContext, useState, useContext, ReactNode, useCallback, useEffect } from 'react';
import { UserProfile, UserProfileUpdateRequest, UserProfileResponse, DisplayNameStatus } from '@openmirror/shared';
import { useActualApi } from '../hooks/useActualApi'; 
import { useAuth } from './AuthContext'; 

interface ProfileContextType {
  profile: UserProfile | null;
  isLoadingProfile: boolean;
  isUploadingPicture: boolean; // New state for specific loading
  profileError: string | null;
  uploadError: string | null; // New state for specific error
  fetchProfile: () => Promise<void>;
  updateProfile: (data: UserProfileUpdateRequest) => Promise<UserProfileResponse>;
  uploadProfilePictureFile: (file: File) => Promise<{ success: boolean; message?: string; newProfilePictureUrl?: string }>;
}

const ProfileContext = createContext<ProfileContextType | undefined>(undefined);

export const ProfileProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { idToken } = useAuth(); 
  const { getOperatorProfile, updateOperatorProfile: apiUpdateProfile, uploadProfilePicture: apiUploadProfilePicture } = useActualApi();
  
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [isLoadingProfile, setIsLoadingProfile] = useState(false);
  const [isUploadingPicture, setIsUploadingPicture] = useState(false);
  const [profileError, setProfileError] = useState<string | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const performFetchProfile = useCallback(async () => {
    if (!idToken) {
      setProfile(null); 
      setIsLoadingProfile(false);
      setProfileError(null);
      return;
    }

    setIsLoadingProfile(true);
    setProfileError(null);
    try {
      const response = await getOperatorProfile(); 
      if (response.success && response.profile) {
        setProfile(response.profile);
      } else {
        setProfileError(response.message || "Failed to load profile");
        setProfile(null);
      }
    } catch (e: any) {
      setProfileError(e.message || "An error occurred while fetching the profile");
      console.error("fetchUserProfile error:", e);
      setProfile(null);
    } finally {
      setIsLoadingProfile(false);
    }
  }, [idToken, getOperatorProfile]);

  useEffect(() => {
    if (idToken) {
      performFetchProfile();
    } else {
      setProfile(null);
      setIsLoadingProfile(false); // Ensure loader off on logout
      setProfileError(null);
    }
  }, [idToken, performFetchProfile]);

  const updateProfile = useCallback(async (data: UserProfileUpdateRequest): Promise<UserProfileResponse> => {
    if (!idToken) {
      const errorMsg = "User not authenticated for profile update.";
      setProfileError(errorMsg);
      // Return structure consistent with what frontend expects on error
      return { success: false, message: errorMsg, profile: profile || undefined }; 
    }
    
    setIsLoadingProfile(true); 
    setProfileError(null);

    try {
      const response = await apiUpdateProfile(data); 
      if (response.success) {
        if (response.profile) {
          setProfile(response.profile);
        } else {
          // Optimistically apply the submitted changes to avoid a refetch that races
          // with Meta's API propagation (which would still show the old approved state).
          setProfile(prev => {
            if (!prev) return prev;
            const updated: UserProfile = { ...prev };
            if (data.displayName !== undefined) updated.displayName = data.displayName || prev.displayName;
            if (data.description !== undefined) updated.description = data.description ?? prev.description;
            if (data.about !== undefined) updated.about = data.about ?? prev.about;
            if (data.email !== undefined) updated.email = data.email ?? prev.email;
            if (data.address !== undefined) updated.address = data.address ?? prev.address;
            if (data.website !== undefined) updated.website = data.website ?? prev.website;
            // If display name changed, mark PENDING — it's been submitted to Meta for review
            if (data.displayName && data.displayName !== prev.displayName) {
              updated.displayNameStatus = DisplayNameStatus.PENDING;
            }
            return updated;
          });
        }
      } else {
        setProfileError(response.message || "Failed to update profile");
      }
      return response; 
    } catch (e: any) {
      const errorMsg = e.message || "An error occurred while updating the profile";
      setProfileError(errorMsg);
      console.error("updateProfile error:", e);
      return { success: false, message: errorMsg, profile: profile || undefined };
    } finally {
      setIsLoadingProfile(false); 
    }
  }, [idToken, apiUpdateProfile, profile]);

  const uploadProfilePictureFile = useCallback(async (file: File): Promise<{ success: boolean; message?: string; newProfilePictureUrl?: string }> => {
    if (!idToken) {
      const errorMsg = "User not authenticated for profile picture upload.";
      setUploadError(errorMsg);
      return { success: false, message: errorMsg };
    }

    setIsUploadingPicture(true);
    setUploadError(null);

    try {
      const response = await apiUploadProfilePicture(file);
      if (response.success && response.newProfilePictureUrl) {
        setProfile(prevProfile => {
          if (prevProfile) {
            return { ...prevProfile, profilePictureUrl: response.newProfilePictureUrl! };
          }
          return null; // Should ideally not happen if upload is attempted after profile load
        });
        // Clear general profile error if upload is successful
        setProfileError(null);
      } else {
        setUploadError(response.message || "Failed to upload profile picture.");
      }
      return { ...response, newProfilePictureUrl: response.newProfilePictureUrl };
    } catch (e: any) {
      const errorMsg = e.message || "An error occurred while uploading the profile picture.";
      setUploadError(errorMsg);
      console.error("uploadProfilePictureFile error:", e);
      return { success: false, message: errorMsg };
    } finally {
      setIsUploadingPicture(false);
    }
  }, [idToken, apiUploadProfilePicture]);

  return (
    <ProfileContext.Provider value={{ 
      profile, 
      isLoadingProfile, 
      isUploadingPicture,
      profileError, 
      uploadError,
      fetchProfile: performFetchProfile, 
      updateProfile,
      uploadProfilePictureFile
    }}>
      {children}
    </ProfileContext.Provider>
  );
};

export const useProfile = (): ProfileContextType => {
  const context = useContext(ProfileContext);
  if (context === undefined) {
    throw new Error('useProfile must be used within a ProfileProvider');
  }
  return context;
};